import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, mediaUrl, fmtEUR } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Camera, Search, X, Plus, Minus, Save, MapPin, Package, ArrowLeft, ScanLine, RefreshCw, Check, DollarSign } from "lucide-react";
import { formatLocDisplay } from "@/components/WarehouseExtras";

export default function ScanPage() {
  const { user } = useApp();
  const nav = useNavigate();
  const [mode, setMode] = useState("idle"); // idle | scanning | product | list | empty
  const [q, setQ] = useState("");
  const [product, setProduct] = useState(null);
  const [locations, setLocations] = useState([]);
  const [ctx, setCtx] = useState(null); // scanned position context
  const [actionHint, setActionHint] = useState(null); // "add" | "remove" | null
  const [results, setResults] = useState([]);
  const [emptyLoc, setEmptyLoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const scannerRef = useRef(null);
  const containerId = "qr-reader-container";

  useEffect(() => {
    if (!user) nav("/login");
  }, [user, nav]);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch {}
      scannerRef.current = null;
    }
  };

  const startScanner = async () => {
    setMode("scanning");
    setTimeout(async () => {
      const mod = await import("html5-qrcode");
      const { Html5Qrcode, Html5QrcodeSupportedFormats } = mod;
      const html5Qr = new Html5Qrcode(containerId, {
        verbose: false,
        formatsToSupport: [
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.CODE_93,
          Html5QrcodeSupportedFormats.ITF,
        ],
      });
      scannerRef.current = html5Qr;
      try {
        await html5Qr.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 280, height: 200 } },
          (decodedText) => {
            html5Qr.pause(true);
            lookup(decodedText);
          },
          () => {}
        );
      } catch (e) {
        toast.error("Nuk mund të hapet kamera. Përdor kërkimin manual.");
        setMode("idle");
      }
    }, 100);
  };

  useEffect(() => () => { stopScanner(); }, []);

  const lookup = async (val) => {
    const query = (val || q).trim();
    if (!query) return;
    setLoading(true);
    await stopScanner();
    try {
      const r = await api.get(`/admin/scan/lookup?q=${encodeURIComponent(query)}`);
      const d = r.data;
      if (d.type === "product") {
        setProduct(d.product); setLocations(d.locations); setCtx(d.context_location);
        setActionHint(d.action_hint || null);
        setMode("product");
      } else if (d.type === "empty_position") {
        setEmptyLoc(d.location); setMode("empty");
      } else if (d.type === "list") {
        setResults(d.results); setMode("list");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nuk u gjet");
      setMode("idle");
    } finally { setLoading(false); }
  };

  const reset = () => {
    setMode("idle"); setProduct(null); setLocations([]); setCtx(null); setResults([]); setEmptyLoc(null); setQ(""); setActionHint(null);
    stopScanner();
  };

  return (
    <div className="fixed inset-0 bg-[#131E30] text-white z-40 overflow-y-auto" data-testid="scan-page">
      <div className="sticky top-0 bg-black/95 backdrop-blur border-b border-[#1F2B44] px-3 py-3 flex items-center justify-between z-10">
        <div className="flex items-center gap-2">
          <ScanLine className="text-[#FFD600]" />
          <div className="font-display text-lg uppercase">Skano Stokun</div>
        </div>
        <Link to="/admin/dollapet" className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1" data-testid="scan-close">
          <X size={16} />
        </Link>
      </div>

      {/* IDLE — search input + scan button */}
      {mode === "idle" && (
        <div className="p-4 max-w-lg mx-auto space-y-4">
          <div>
            <label className="text-xs uppercase text-neutral-400 tracking-widest">SKU, Nr. Artikulli, ose emër</label>
            <div className="relative mt-2">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                value={q} onChange={e => setQ(e.target.value)}
                onKeyDown={e => e.key === "Enter" && lookup()}
                autoFocus data-testid="scan-input"
                placeholder="p.sh. SKU-1C8ABB ose motor"
                className="w-full h-14 bg-[#131E30] border border-[#1F2B44] pl-10 pr-3 text-lg"
              />
            </div>
            <button onClick={() => lookup()} disabled={!q.trim() || loading} data-testid="scan-search-btn"
              className="hg-btn-primary w-full h-12 mt-2 uppercase text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40">
              <Search size={16} /> Kërko
            </button>
          </div>
          <div className="text-center text-neutral-500 text-xs uppercase">ose</div>
          <button onClick={startScanner} data-testid="scan-camera-btn"
            className="w-full h-16 border-2 border-[#FFD600] text-[#FFD600] uppercase text-sm inline-flex items-center justify-center gap-2 hover:bg-[#FFD600]/10">
            <Camera size={20} /> Hap Kamerën & Skano QR/Barkod
          </button>
          <div className="text-[11px] text-neutral-500 text-center">
            Skano etiketë QR ose barkod (UPC/EAN/CODE-128) — ose fut kodin manualisht
          </div>
        </div>
      )}

      {/* SCANNING */}
      {mode === "scanning" && (
        <div className="p-4 max-w-lg mx-auto">
          <div id={containerId} className="w-full aspect-square bg-[#131E30] border-2 border-[#FFD600]" />
          <button onClick={reset} data-testid="scan-cancel"
            className="w-full h-12 mt-3 border border-[#1F2B44] uppercase text-xs hover:bg-[#262626]">
            Anulo
          </button>
        </div>
      )}

      {/* EMPTY POSITION */}
      {mode === "empty" && emptyLoc && (
        <div className="p-4 max-w-lg mx-auto text-center space-y-3">
          <div className="text-emerald-400 font-display text-2xl">Pozicion i lirë</div>
          <div className="font-mono text-lg text-[#FFD600]">{emptyLoc.cabinet}-{emptyLoc.drawer}-{emptyLoc.position}</div>
          <div className="text-sm text-neutral-400">Ky pozicion s'ka produkt. Kërko një produkt për ta vendosur këtu.</div>
          <button onClick={reset} className="hg-btn-primary w-full h-12 uppercase text-sm">Kthehu</button>
        </div>
      )}

      {/* LIST — multiple results */}
      {mode === "list" && (
        <div className="p-4 max-w-lg mx-auto space-y-2" data-testid="scan-results-list">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-neutral-400">{results.length} rezultate për "{q}"</div>
            <button onClick={reset} className="text-xs text-[#FFD600]">Kërko sërish</button>
          </div>
          {results.length === 0 && <div className="text-neutral-500 py-8 text-center">Asgjë nuk u gjet</div>}
          {results.map(r => (
            <button key={r.id} onClick={() => lookup(r.sku || r.article_number || r.id)}
              data-testid={`scan-result-${r.id}`}
              className="w-full flex gap-3 items-center border border-[#1F2B44] p-3 text-left hover:bg-[#0f0f0f]">
              {r.images?.[0] && <img src={mediaUrl(r.images[0])} alt="" className="w-14 h-14 object-contain bg-[#131E30] border border-[#1F2B44] shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{r.name}</div>
                <div className="text-xs text-neutral-500 truncate">{r.sku} • {r.article_number}</div>
                <div className="text-xs"><span className="text-[#FFD600]">{fmtEUR(r.price)}</span> • <span className="text-emerald-400">{r.stock_qty} {r.unit || "cp"}</span></div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* PRODUCT — full detail with edits */}
      {mode === "product" && product && (
        <ProductPanel
          product={product} locations={locations} ctx={ctx} actionHint={actionHint}
          onReset={reset}
          onRefresh={() => lookup(product.sku || product.article_number || product.id)}
        />
      )}
    </div>
  );
}

function ProductPanel({ product, locations, ctx, actionHint, onReset, onRefresh }) {
  const [locIdx, setLocIdx] = useState(0);
  const [qtyDelta, setQtyDelta] = useState(1);
  const [price, setPrice] = useState(product.price);
  const [discount, setDiscount] = useState(product.discount_price ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ctx) {
      const idx = locations.findIndex(l => l.cabinet === ctx.cabinet && l.drawer === ctx.drawer && l.position === ctx.position);
      if (idx >= 0) setLocIdx(idx);
    }
  }, [ctx, locations]);

  const currentLoc = locations[locIdx];

  const applyDelta = async (sign) => {
    if (!currentLoc) return toast.error("Zgjidh një lokacion");
    setSaving(true);
    try {
      const url = sign > 0 ? "add" : "remove";
      await api.post(`/admin/products/${product.id}/stock/${url}`, {
        qty: qtyDelta,
        cabinet: currentLoc.cabinet, drawer: currentLoc.drawer, position: currentLoc.position,
        note: "Skanim celular",
      });
      toast.success(`${sign > 0 ? "+" : "−"}${qtyDelta} ${product.unit || "copë"}`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gabim");
    } finally { setSaving(false); }
  };

  const savePrice = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/products/${product.id}/quick`, {
        price: Number(price),
        discount_price: discount === "" ? null : Number(discount),
      });
      toast.success("Çmimi u ruajt");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gabim");
    } finally { setSaving(false); }
  };

  const img = product.images?.[0];
  const totalQty = locations.reduce((s, l) => s + l.qty, 0);

  return (
    <div className="p-4 max-w-lg mx-auto space-y-4" data-testid="scan-product-panel">
      {/* Product header */}
      <div className="hg-card p-4 flex gap-3">
        {img && <img src={mediaUrl(img)} alt="" className="w-24 h-24 object-contain bg-[#131E30] border border-[#1F2B44] shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl uppercase leading-tight">{product.name}</div>
          <div className="text-xs text-neutral-400 mt-1">{product.brand} • {product.model}</div>
          <div className="text-xs text-neutral-500 mt-1">
            {product.sku && <div>SKU: {product.sku}</div>}
            {product.article_number && <div>Art: {product.article_number}</div>}
          </div>
        </div>
      </div>

      {/* Stock summary */}
      <div className="hg-card p-4">
        <div className="text-xs uppercase text-neutral-400 tracking-widest mb-2 flex items-center gap-2">
          <Package size={14} /> Stoku aktual
        </div>
        <div className="font-display text-5xl text-emerald-400" data-testid="scan-stock-total">{totalQty}</div>
        <div className="text-sm text-neutral-400">{product.unit || "copë"}</div>
      </div>

      {/* Location selector */}
      {locations.length > 0 && (
        <div className="hg-card p-4">
          <div className="text-xs uppercase text-neutral-400 tracking-widest mb-2 flex items-center gap-2">
            <MapPin size={14} /> Lokacioni {ctx && <span className="text-[#FFD600] normal-case">(skanuar)</span>}
          </div>
          {locations.length === 1 ? (
            <div className="font-mono text-lg text-[#FFD600]">{currentLoc.cabinet}-{currentLoc.drawer}-{currentLoc.position} • {currentLoc.qty} {product.unit || "cp"}</div>
          ) : (
            <select value={locIdx} onChange={e => setLocIdx(Number(e.target.value))} data-testid="scan-loc-select"
              className="w-full h-11 bg-[#131E30] border border-[#1F2B44] px-3 font-mono">
              {locations.map((l, i) => (
                <option key={i} value={i}>{l.cabinet}-{l.drawer}-{l.position} — {l.qty} {product.unit || "cp"}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Qty +/- controls */}
      {locations.length > 0 && (
        <div className="hg-card p-4 space-y-3">
          <div className="text-xs uppercase text-neutral-400 tracking-widest flex items-center gap-2">
            <Package size={14} /> Sasia për të ndryshuar
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setQtyDelta(Math.max(1, qtyDelta - 1))} className="w-12 h-14 border border-[#1F2B44] flex items-center justify-center"><Minus /></button>
            <input type="number" min="1" value={qtyDelta} onChange={e => setQtyDelta(Math.max(1, +e.target.value))} data-testid="scan-qty-delta"
              className="flex-1 h-14 bg-[#131E30] border border-[#1F2B44] text-center text-2xl font-bold" />
            <button onClick={() => setQtyDelta(qtyDelta + 1)} className="w-12 h-14 border border-[#1F2B44] flex items-center justify-center"><Plus /></button>
          </div>
          <div className="text-sm text-neutral-400 text-center">
            {currentLoc && `Pas veprimit: në lokacion ${Math.max(0, currentLoc.qty - qtyDelta)}—${currentLoc.qty + qtyDelta} ${product.unit || "cp"}`}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => applyDelta(1)} disabled={saving || !currentLoc} data-testid="scan-add-btn"
              className={`h-14 uppercase text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 ${
                actionHint === "add"
                  ? "bg-emerald-500 text-black font-bold border-2 border-emerald-500 hover:bg-emerald-400 ring-4 ring-emerald-500/40"
                  : "border-2 border-emerald-600 text-emerald-400 hover:bg-emerald-900/40"
              }`}>
              <Plus size={18} /> Shto {qtyDelta}
            </button>
            <button onClick={() => applyDelta(-1)} disabled={saving || !currentLoc} data-testid="scan-remove-btn"
              className={`h-14 uppercase text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 ${
                actionHint === "remove"
                  ? "bg-red-600 text-white font-bold border-2 border-red-600 hover:bg-red-500 ring-4 ring-red-500/40"
                  : "border-2 border-red-700 text-red-400 hover:bg-red-900/30"
              }`}>
              <Minus size={18} /> Nxirr {qtyDelta}
            </button>
          </div>
          {actionHint && (
            <div className={`text-center text-xs uppercase tracking-widest ${actionHint === "add" ? "text-emerald-400" : "text-red-400"}`}>
              {actionHint === "add" ? "→ Tiketë Vendosje: kliko SHTO" : "→ Tiketë Dorëzimi: kliko NXIRR"}
            </div>
          )}
        </div>
      )}

      {/* Price editor */}
      <div className="hg-card p-4 space-y-3">
        <div className="text-xs uppercase text-neutral-400 tracking-widest flex items-center gap-2">
          <DollarSign size={14} /> Çmimi
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[11px] text-neutral-500">Çmimi bazë (€)</label>
            <input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} data-testid="scan-price-input"
              className="mt-1 w-full h-12 bg-[#131E30] border border-[#1F2B44] px-3 text-lg font-bold text-[#FFD600]" />
          </div>
          <div>
            <label className="text-[11px] text-neutral-500">Zbritje (€)</label>
            <input type="number" step="0.01" value={discount} onChange={e => setDiscount(e.target.value)} data-testid="scan-discount-input"
              placeholder="opsional"
              className="mt-1 w-full h-12 bg-[#131E30] border border-[#1F2B44] px-3 text-lg font-bold" />
          </div>
        </div>
        <button onClick={savePrice} disabled={saving} data-testid="scan-save-price"
          className="hg-btn-primary w-full h-12 uppercase text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40">
          <Save size={16} /> Ruaj çmimin
        </button>
      </div>

      {locations.length === 0 && (
        <div className="hg-card p-4 text-center text-sm text-neutral-400">
          Ky produkt s'ka lokacion. Hape produktin për ta vendosur në stok.
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2 pb-6">
        <button onClick={onReset} data-testid="scan-new-btn"
          className="h-12 border border-[#1F2B44] uppercase text-xs inline-flex items-center justify-center gap-1 hover:bg-[#262626]">
          <ScanLine size={14} /> Skano tjetër
        </button>
        <Link to={`/produkt/${product.id}`} data-testid="scan-open-full"
          className="h-12 border-2 border-[#FFD600] text-[#FFD600] uppercase text-xs inline-flex items-center justify-center gap-1">
          Hap plotësisht
        </Link>
      </div>
    </div>
  );
}
